/* ────────────────────────────────────────────────────────────────────────────
 * packages/shared/src/permissions.ts
 *
 * The 35 permission strings, the 7 SYSTEM role codes, and the PRD §3.2 / §3.4
 * seed matrix.
 *
 * ⚠ ROLES ARE DATA. `RoleCode` is `string`; an Admin creates roles at runtime.
 * Everything in this module that mentions a role by name concerns only the SEVEN
 * SYSTEM ROLES the seed installs — it is the seed's and the tests' source of
 * truth and must never be consulted for runtime authorization. Authorization
 * resolves `role → role_permission → permission` out of PostgreSQL
 * (`UsersService.permissionsForRoles`), which is the only place that knows about
 * a role created after the build.
 * Spec: docs/spec/12-api-contract.md §3.3 (the strings and their derivation),
 *       §3.4 (the role → permission seed), §3.5 (the guard);
 *       docs/spec/README.md §4 conflict #7 (DOTS, not colons) and #9 (ADMIN).
 *
 * ── Where the canonical declarations live ───────────────────────────────────
 * `Permission`, `RoleCode`, `SystemRoleCode`, `SYSTEM_ROLE_CODES` and
 * `SYSTEM_ROLE_PERMISSIONS` are declared ONCE, in `api-types.ts` (transcribed
 * from 12-api-contract.md §12), and re-exported here so that
 * `@cropmate/shared/permissions` is a complete module. Restating them would put
 * two copies of a 35 × 7 matrix in one package, and the copies would drift.
 *
 * What this module adds is the part `api-types.ts` cannot express:
 *   • `PERMISSIONS` — the 35 strings as RUNTIME data, exhaustiveness-checked by
 *     the compiler, so a permission added to the union but forgotten here is a
 *     build error rather than a missing seed row.
 *   • `PERMISSION_MATRIX` — §3.4 transcribed ROW-WISE, i.e. laid out exactly as
 *     the spec table is (one line per permission, listing the roles that hold
 *     it). `SYSTEM_ROLE_PERMISSIONS` is the same data COLUMN-WISE. Two independent
 *     transcriptions of one table, proved equal by `permissions.test.ts`: a
 *     single mis-keyed `●` cannot survive both.
 *   • the guard helpers the API and the web app both need.
 * ──────────────────────────────────────────────────────────────────────────── */
import { SYSTEM_ROLE_CODES, SYSTEM_ROLE_PERMISSIONS, ROLE_LANDING_ROUTE } from './api-types.js';
export { SYSTEM_ROLE_CODES, SYSTEM_ROLE_PERMISSIONS, ROLE_LANDING_ROUTE };
/* ═══════════════════════════ 1. THE CLOSED SETS ═════════════════════════════ */
/**
 * Exhaustive key set. `Record<Permission, true>` means a permission added to the
 * union and not added here is a COMPILE error, and a string here that is not in
 * the union is a compile error too. Order is the §3.3 order and is preserved by
 * `Object.keys`, so `PERMISSIONS` is deterministic.
 */
const PERMISSION_KEYS = {
    /* Group 1 — the eleven capabilities of PRD §3.2. */
    'job.write': true,
    'roster.confirm': true,
    'worker.write': true,
    'rate.write': true,
    'paycomponent.write': true,
    'payroll.run': true,
    'payroll.lock': true,
    'payroll.reopen': true,
    'disbursement.write': true,
    'audit.read': true,
    /* Group 2 — derived read scopes and finer verbs (§3.3). */
    'job.read': true,
    'worker.read': true,
    'document.read': true,
    'document.write': true,
    'document.file.read': true,
    'team.read': true,
    'team.write': true,
    'agent.read': true,
    'agent.write': true,
    'rate.read': true,
    'worktype.write': true,
    'casual.read': true,
    'casual.write': true,
    'paycomponent.read': true,
    'period.manage': true,
    'payroll.read': true,
    'disbursement.read': true,
    'export.generate': true,
    'report.read': true,
    'baseline.upload': true,
    'config.read': true,
    'config.write': true,
    'user.read': true,
    'user.manage': true,
    'company.manage': true,
};
/** All 35 permission strings, in §3.3 order. */
export const PERMISSIONS = Object.freeze(Object.keys(PERMISSION_KEYS));
/** The eleven strings that map 1:1 onto a PRD §3.2 matrix row. */
export const MATRIX_CAPABILITIES = Object.freeze([
    'job.write',
    'roster.confirm',
    'worker.write',
    'rate.write',
    'paycomponent.write',
    'payroll.run',
    'payroll.lock',
    'payroll.reopen',
    'disbursement.write',
    'audit.read',
]);
const SYSTEM_ROLE_KEYS = {
    CLERK: true,
    SUPERVISOR: true,
    HR: true,
    PAYROLL: true,
    FINANCE: true,
    ADMIN: true,
    AUDITOR: true,
};
/**
 * The shape a role code must have: UPPER_SNAKE, starting with a letter, at most
 * 32 characters (`role.code` is `varchar(32)`).
 *
 * There is no closed list to check against — roles are data — so this is the
 * only STATIC validation possible. Existence is a database question.
 */
export const ROLE_CODE_PATTERN = /^[A-Z][A-Z0-9]*(_[A-Z0-9]+)*$/;
export const ROLE_CODE_MAX_LENGTH = 32;
/* ═══════════════════════════ 2. THE §3.4 MATRIX, ROW-WISE ═══════════════════ */
/**
 * `12-api-contract.md` §3.4 transcribed one line per table row. A `●` in the
 * spec's grid is a role code in the array. Compare against `SYSTEM_ROLE_PERMISSIONS`
 * (the same table, column-wise) — `permissions.test.ts` asserts they agree.
 *
 * **The deliberate gaps in the ADMIN column** (README §4 conflict #9,
 * `docs/open-questions.md`, item A). PRD §3.2 leaves the Admin cell blank on *Approve & lock run*
 * and *Disburse / export files*, so ADMIN holds NEITHER `payroll.lock`,
 * `disbursement.write` nor `export.generate`. It DOES hold `payroll.reopen` —
 * reopening a locked run is the operator's job, not Finance's. Admin is the
 * operator of the system, not a second Finance department: granting those would
 * let one account both run and lock a payroll, defeating the one separation of
 * duty this build still enforces.
 * The prototype's "Admin can do everything above" is a prototype simplification
 * and is on the client sign-off list.
 *
 * `AUDITOR` holds read permissions only. There is no write permission anywhere
 * in its column, and `PermissionGuard` is the only thing enforcing that — there
 * is no separate "read-only mode".
 */
export const PERMISSION_MATRIX = Object.freeze({
    'job.read': ['CLERK', 'SUPERVISOR', 'PAYROLL', 'FINANCE', 'ADMIN', 'AUDITOR'],
    'job.write': ['CLERK', 'SUPERVISOR', 'ADMIN'],
    'roster.confirm': ['SUPERVISOR', 'ADMIN'],
    'worker.read': ['CLERK', 'SUPERVISOR', 'HR', 'PAYROLL', 'FINANCE', 'ADMIN', 'AUDITOR'],
    'worker.write': ['HR', 'ADMIN'],
    'document.read': ['SUPERVISOR', 'HR', 'PAYROLL', 'FINANCE', 'ADMIN', 'AUDITOR'],
    'document.write': ['HR', 'ADMIN'],
    'document.file.read': ['HR', 'ADMIN'],
    'team.read': ['CLERK', 'SUPERVISOR', 'HR', 'PAYROLL', 'FINANCE', 'ADMIN', 'AUDITOR'],
    'team.write': ['HR', 'ADMIN'],
    'agent.read': ['HR', 'PAYROLL', 'FINANCE', 'ADMIN', 'AUDITOR'],
    'agent.write': ['HR', 'ADMIN'],
    'rate.read': ['CLERK', 'SUPERVISOR', 'PAYROLL', 'FINANCE', 'ADMIN', 'AUDITOR'],
    // Was `rate.propose` (SUPERVISOR, PAYROLL, ADMIN) + `rate.approve` (FINANCE).
    // FR-3.5's propose-then-approve was dropped at the client's request (ADR-0002).
    // SUPERVISOR drops to read-only: proposing was a request for someone else to
    // decide, and turning that into an immediately-effective write would be a
    // larger grant than the role previously had.
    'rate.write': ['PAYROLL', 'FINANCE', 'ADMIN'],
    'worktype.write': ['PAYROLL', 'ADMIN'],
    'casual.read': ['CLERK', 'HR', 'PAYROLL', 'FINANCE', 'ADMIN', 'AUDITOR'],
    'casual.write': ['CLERK', 'PAYROLL', 'ADMIN'],
    'paycomponent.read': ['HR', 'PAYROLL', 'FINANCE', 'ADMIN', 'AUDITOR'],
    'paycomponent.write': ['HR', 'PAYROLL', 'ADMIN'],
    'period.manage': ['PAYROLL', 'ADMIN'],
    'payroll.read': ['PAYROLL', 'FINANCE', 'ADMIN', 'AUDITOR'],
    'payroll.run': ['PAYROLL', 'ADMIN'],
    'payroll.lock': ['FINANCE'], // NOT ADMIN — §3.4, conflict #9
    'payroll.reopen': ['ADMIN'], // ADMIN ONLY — FR-6.7
    'disbursement.read': ['PAYROLL', 'FINANCE', 'ADMIN', 'AUDITOR'],
    'disbursement.write': ['FINANCE'], // NOT ADMIN — §3.4, conflict #9
    'export.generate': ['FINANCE'], // NOT ADMIN — §3.4, conflict #9
    'report.read': ['SUPERVISOR', 'HR', 'PAYROLL', 'FINANCE', 'ADMIN', 'AUDITOR'],
    'baseline.upload': ['PAYROLL', 'ADMIN'],
    'config.read': ['CLERK', 'SUPERVISOR', 'HR', 'PAYROLL', 'FINANCE', 'ADMIN', 'AUDITOR'],
    'config.write': ['ADMIN'],
    'user.read': ['ADMIN', 'AUDITOR'],
    'user.manage': ['ADMIN'],
    'audit.read': ['FINANCE', 'ADMIN', 'AUDITOR'],
    // Creating a tenant is more powerful than creating a user inside one, so this
    // is NOT folded into `user.manage`. Membership assignment rides with it.
    'company.manage': ['ADMIN'],
});
/**
 * Transpose `PERMISSION_MATRIX` into the column-wise shape of
 * `SYSTEM_ROLE_PERMISSIONS`. The result is sorted by the §3.3 permission order,
 * which is the order `PERMISSIONS` is in, so two transpositions of the same
 * table are comparable element-by-element.
 */
export function rolePermissionsFromMatrix() {
    const out = {};
    for (const role of SYSTEM_ROLE_CODES)
        out[role] = [];
    for (const permission of PERMISSIONS) {
        for (const role of PERMISSION_MATRIX[permission])
            out[role].push(permission);
    }
    return out;
}
/* ═══════════════════════════ 3. GUARD HELPERS ═══════════════════════════════ */
export const isPermission = (v) => typeof v === 'string' && Object.prototype.hasOwnProperty.call(PERMISSION_KEYS, v);
/**
 * Does `v` LOOK like a role code? Roles are data, so this is a shape check, not
 * a membership check — a custom role created this morning is a perfectly valid
 * role code and no closed list could contain it.
 *
 * Used on the JWT decode path, where the alternative is trusting an arbitrary
 * string, and on `POST /roles`, where it is the format rule the contract states.
 */
export const isRoleCode = (v) => typeof v === 'string' && v.length <= ROLE_CODE_MAX_LENGTH && ROLE_CODE_PATTERN.test(v);
/** Is `v` one of the seven PRD §3.1 roles the seed installs? */
export const isSystemRoleCode = (v) => typeof v === 'string' && Object.prototype.hasOwnProperty.call(SYSTEM_ROLE_KEYS, v);
/** The SEEDED permissions of one system role, in §3.3 order.
 *  ⚠ Seed and tests only — see `SYSTEM_ROLE_PERMISSIONS`. */
export const permissionsForRole = (role) => SYSTEM_ROLE_PERMISSIONS[role];
/** The UNION of several SYSTEM roles' seeded permissions, deduplicated and
 *  returned in the deterministic §3.3 order.
 *  ⚠ Seed and tests only. Runtime authorization resolves the database instead;
 *  a custom role has no entry here and would silently contribute nothing. */
export function permissionsForRoles(roles) {
    const held = new Set();
    for (const role of roles)
        for (const p of SYSTEM_ROLE_PERMISSIONS[role])
            held.add(p);
    return PERMISSIONS.filter((p) => held.has(p));
}
export const hasPermission = (held, required) => held.includes(required);
/** Multiple permissions on one handler are AND-ed (§3.5). */
export const hasAllPermissions = (held, required) => required.every((p) => held.includes(p));
/** OR semantics exist only where a route deliberately widens; the guard itself
 *  never ORs (§3.5). Provided for nav-visibility checks in the web app. */
export const hasAnyPermission = (held, required) => required.some((p) => held.includes(p));
/** The permissions in `required` that `held` is missing, in the order asked for.
 *  `PermissionGuard` reports `missing[0]` as `meta.requiredPermission`. */
export const missingPermissions = (held, required) => required.filter((p) => !held.includes(p));
//# sourceMappingURL=permissions.js.map